import { Component, OnInit, OnDestroy, inject, ChangeDetectionStrategy } from '@angular/core';
import { combineLatest, Subscription, take } from 'rxjs';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Authorization } from 'src/app/model/Authorization';
import { TransactionEvent } from 'src/app/model/TransactionEvent';
import { AuthorizationService } from 'src/app/services/authorization/authorization.service';

@Component({
    selector: 'app-borrower-authorizations-table',
    templateUrl: './borrower-authorizations-table.component.html',
    styleUrl: './borrower-authorizations-table.component.scss',
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BorrowerAuthorizationsTableComponent implements OnInit, OnDestroy {
    /** Dependency injections */
    private authorizationService: AuthorizationService = inject(AuthorizationService);

    /** Authorizations and transaction events to render */
    authorizations: Authorization[] = [];
    transactionEvents: TransactionEvent[] = [];

    /** Loading state */
    loading = true;

    /** Table columns - commented out fields come from @product */
    displayedColumns: string[] = [
        'expand',
        //'id',
        'supplier_company',
        'created_at',
        'total_amount',
        'captured_amount',
        'voided_amount',
        'remaining_amount',
    ];

    /** Embedded transaction events table columns */
    transactionEventColumns: string[] = [
        'id',
        'statusTitleCase',
        'amount',
        'memo',
        'last_updated_at',
    ];

    /** Track expanded rows (by Authorization id) */
    expandedElements: string[] = [];

    /** Subscription for data */
    private authorizationsSub?: Subscription;
    private transactionEventsSub?: Subscription;

    /**
     * OnInit, subscribe to data changes and initialize data source
     */
    ngOnInit(): void {
        // Subscribe to authorizations
        this.authorizationsSub = this.authorizationService.authorizations$.subscribe((authorizations: Authorization[]) => {
            this.authorizations = authorizations;
            this.loading = false;
        });

        // Subscribe to transaction events
        this.transactionEventsSub = this.authorizationService.transactionEvents$.subscribe((transactionEvents: TransactionEvent[]) => {
            this.transactionEvents = transactionEvents;
        });

        // Initial data load
        const authorizations = this.authorizationService.getAuthorizations();
        const transactionEvents = this.authorizationService.getTransactionEvents();
        if (authorizations && transactionEvents) {
            this.authorizations = authorizations;
            this.transactionEvents = transactionEvents;
            this.loading = false;
        }
    }

    /**
     * On component destroy, unsubscribe from all subscriptions
     */
    ngOnDestroy(): void {
        if (this.authorizationsSub) {
            this.authorizationsSub.unsubscribe();
        }
        if (this.transactionEventsSub) {
            this.transactionEventsSub.unsubscribe();
        }
    }

    /**
     * Toggle row expansion
     * 
     * @param element The authorization row to toggle
     */
    toggleRow(element: Authorization): void {
        this.expandedElements.includes(element.id) ? this.expandedElements.splice(this.expandedElements.indexOf(element.id), 1) : this.expandedElements.push(element.id);
    }

    /**
     * Check if a row is expanded
     * 
     * @param element The authorization row to check
     * @return Whether the row is expanded
     */
    isExpanded(element: Authorization): boolean {
        return this.expandedElements.includes(element.id);
    }

    /**
     * Get transaction events for an authorization
     * 
     * @param authorization The authorization to get transaction events for
     */
    getTransactionEvents(authorization: Authorization): TransactionEvent[] {
        return authorization.getTransactionEvents();
    }

    /**
     * Format currency values
     * 
     * @param amount The amount to format
     * @return The formatted currency string
     */
    formatCurrency(amount: number): string {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    }

    /**
     * Format date values
     * 
     * @param date The date to format
     * @return The formatted date string
     */
    formatDate(date: Date | null): string {
        if (!date) return 'N/A';
        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }).format(new Date(date));
    }
}
